import React from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import { PictureAsPdf, Download, Description } from '@mui/icons-material';

/**
 * Reusable ExportButtons component for consistent export UI across all pages.
 *
 * Props:
 *   onExportPDF   - callback for PDF export  (button shown only if provided)
 *   onExportExcel - callback for Excel export (button shown only if provided)
 *   onExportWord  - callback for Word export  (button shown only if provided)
 *   isExporting   - boolean, shows spinner / disables all buttons while true
 *   disabled      - additional disabled condition (e.g. no data)
 *   size          - 'small' (default) | 'medium'
 *   pdfLabel      - optional label override for PDF button (default 'Export PDF')
 *   excelLabel    - optional label override for Excel button (default 'Export Excel')
 *   wordLabel     - optional label override for Word button (default 'Export Word')
 */
const ExportButtons = ({
    onExportPDF,
    onExportExcel,
    onExportWord,
    isExporting = false,
    disabled = false,
    size = 'small',
    pdfLabel = 'Export PDF',
    excelLabel = 'Export Excel',
    wordLabel = 'Export Word',
}) => {
    const isDisabled = isExporting || disabled;

    const commonSx = {
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 2,
        height: size === 'small' ? 36 : 42,
        px: size === 'small' ? 2 : 2.5,
    };

    return (
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {onExportPDF && (
                <Button
                    variant="contained"
                    size={size}
                    startIcon={
                        isExporting
                            ? <CircularProgress size={16} color="inherit" />
                            : <PictureAsPdf />
                    }
                    onClick={onExportPDF}
                    disabled={isDisabled}
                    sx={{
                        ...commonSx,
                        bgcolor: '#d32f2f',
                        '&:hover': { bgcolor: '#b71c1c' },
                    }}
                >
                    {isExporting ? 'Exporting...' : pdfLabel}
                </Button>
            )}

            {onExportWord && (
                <Button
                    variant="contained"
                    size={size}
                    startIcon={
                        isExporting
                            ? <CircularProgress size={16} color="inherit" />
                            : <Description />
                    }
                    onClick={onExportWord}
                    disabled={isDisabled}
                    sx={{
                        ...commonSx,
                        bgcolor: '#1565c0',
                        '&:hover': { bgcolor: '#0d47a1' },
                    }}
                >
                    {isExporting ? 'Exporting...' : wordLabel}
                </Button>
            )}

            {onExportExcel && (
                <Button
                    variant="contained"
                    size={size}
                    startIcon={
                        isExporting
                            ? <CircularProgress size={16} color="inherit" />
                            : <Download />
                    }
                    onClick={onExportExcel}
                    disabled={isDisabled}
                    sx={{
                        ...commonSx,
                        bgcolor: '#388e3c',
                        '&:hover': { bgcolor: '#1b5e20' },
                    }}
                >
                    {isExporting ? 'Exporting...' : excelLabel}
                </Button>
            )}
        </Box>
    );
};

export default ExportButtons;
